import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { LuArrowRight, LuCheck, LuCopy, LuShieldCheck, LuBuilding2, LuCircleAlert } from 'react-icons/lu';
import PublicLandingPageService, { type PublicPageDetails } from '../services/PublicLandingPageService';
import { getMidiaUrl } from '../services/GerenciarEmpresaService';
import { tintaSobre, razaoDeContraste } from '../utils/contraste';
import './PaginaCaptura.css';

/* Página de captura — a tela que o cliente vê ao ler o QR Code no balcão.
   Direção C: uma pergunta por tela. Desenho fechado em
   docs/designsystem/propostas/pagina-de-captura-publica.html

   Nome e WhatsApp são sempre pedidos; o resto é escolha do lojista, então o
   número de passos vai de 2 a 4 e o contador do topo diz a verdade em qualquer
   combinação. */

type Passo = 'nome' | 'telefone' | 'origem' | 'extras';

/* API mais antiga que este front não manda os interruptores: sem eles, a página
   pergunta tudo — que é exatamente o que ela fazia antes. */
function montarPassos(config: PublicPageDetails | null): Passo[] {
  const pedeOrigem = config?.askSegment ?? true;
  const pedeEmail = config?.askEmail ?? true;
  const pedeCidade = config?.askCity ?? true;
  const passos: Passo[] = ['nome', 'telefone'];
  if (pedeOrigem && (config?.configuredSegments?.length ?? 0) > 0) passos.push('origem');
  if (pedeEmail || pedeCidade) passos.push('extras');
  return passos;
}

interface CupomGuardado {
  codigo: string;
  texto: string | null;
  nome: string;
}

/* O cupom não pode viver só na memória da aba: um refresh, um toque no "voltar"
   ou uma troca de aba que o iOS descarregue apagavam o código para sempre — e o
   jeito de recuperar era se cadastrar de novo, o que dispara um segundo POST. */
const chaveDoCupom = (token: string) => `cap:${token}`;

function lerCupom(token: string): CupomGuardado | null {
  try {
    const cru = localStorage.getItem(chaveDoCupom(token));
    if (!cru) return null;
    const v = JSON.parse(cru);
    if (v && typeof v.codigo === 'string' && typeof v.nome === 'string') return v;
    return null;
  } catch {
    return null;
  }
}

function guardarCupom(token: string, valor: CupomGuardado) {
  try {
    localStorage.setItem(chaveDoCupom(token), JSON.stringify(valor));
  } catch {
    /* Aba anônima ou armazenamento bloqueado: a tela segue funcionando, só não
       sobrevive a um refresh. Não vale interromper o cadastro por isso. */
  }
}

function esquecerCupom(token: string) {
  try {
    localStorage.removeItem(chaveDoCupom(token));
  } catch {
    /* idem */
  }
}

const SEGMENTOS_PADRAO = [
  'Hotéis / Pousada',
  'Instagram',
  'Já conhecia a loja',
  'CAT',
  'Pontos turísticos da cidade',
  'Outros',
];

function formatarTelefone(cru: string): string {
  const d = cru.replace(/\D/g, '').slice(0, 11);
  if (d.length > 10) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length > 6) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length > 2) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length > 0) return `(${d}`;
  return '';
}

const soDigitos = (v: string) => v.replace(/\D/g, '');
const primeiroNome = (v: string) => v.trim().split(/\s+/)[0] || '';

const PublicLandingPage = () => {
  const { token } = useParams<{ token: string }>();

  const [config, setConfig] = useState<PublicPageDetails | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const [indice, setIndice] = useState(0);
  const [erro, setErro] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [origem, setOrigem] = useState('');
  const [origemOutros, setOrigemOutros] = useState('');
  const [email, setEmail] = useState('');
  const [cidade, setCidade] = useState('');

  const [cupom, setCupom] = useState<CupomGuardado | null>(null);
  const [copiado, setCopiado] = useState(false);

  /* O corpo é <form> nos passos e <div> na tela do cupom — o mesmo ref serve
     para levar a rolagem ao topo a cada passo. */
  const corpoRef = useRef<HTMLElement | null>(null);
  const outrosRef = useRef<HTMLInputElement | null>(null);

  const passos = React.useMemo(() => montarPassos(config), [config]);
  const passo = passos[Math.min(indice, passos.length - 1)];
  const total = passos.length;
  const noCupom = cupom !== null;

  const pedeEmail = config?.askEmail ?? true;
  const pedeCidade = config?.askCity ?? true;

  /* ── carga ──────────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!token) return;
    let vivo = true;

    const guardado = lerCupom(token);
    if (guardado) setCupom(guardado);

    PublicLandingPageService.getPageDetails(token)
      .then((dados) => {
        if (vivo) setConfig(dados);
      })
      .catch(() => {
        if (vivo) setFalhou(true);
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });

    return () => {
      vivo = false;
    };
  }, [token]);

  /* A página pública rola por conta própria; o app trava o overflow do body. */
  useEffect(() => {
    const body = document.body.style.overflow;
    const html = document.documentElement.style.overflow;
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = body;
      document.documentElement.style.overflow = html;
    };
  }, []);

  /* O "voltar" do aparelho é o gesto mais comum do celular. Sem isto ele sairia
     da página e levaria o cadastro inteiro junto. Cada passo além do primeiro
     empilha uma entrada; voltar desempilha um passo. */
  useEffect(() => {
    const aoVoltar = (ev: PopStateEvent) => {
      const alvo = (ev.state && typeof ev.state.capPasso === 'number') ? ev.state.capPasso : 0;
      setIndice(alvo);
      setErro(null);
    };
    window.addEventListener('popstate', aoVoltar);
    return () => window.removeEventListener('popstate', aoVoltar);
  }, []);

  /* ── navegação ──────────────────────────────────────────────────────────── */

  const irPara = useCallback((proximo: number) => {
    setIndice(proximo);
    setErro(null);
    window.history.pushState({ capPasso: proximo }, '');
    if (corpoRef.current) corpoRef.current.scrollTop = 0;
  }, []);

  const validar = (): string | null => {
    if (passo === 'nome') {
      if (nome.trim().length < 2) return 'Escreva seu nome para continuar.';
      return null;
    }
    if (passo === 'telefone') {
      const d = soDigitos(telefone);
      if (d.length < 10) return 'Faltam números. O WhatsApp tem DDD e 8 ou 9 dígitos.';
      return null;
    }
    if (passo === 'origem') {
      if (!origem) return 'Escolha uma das opções.';
      if (origem === 'Outros' && origemOutros.trim().length < 2) return 'Conte em poucas palavras como nos conheceu.';
      return null;
    }
    return null;
  };

  const avancar = () => {
    const problema = validar();
    if (problema) {
      setErro(problema);
      return;
    }
    if (indice < total - 1) {
      irPara(indice + 1);
    } else {
      enviar();
    }
  };

  /* O campo de "Outros" nasce embaixo da lista; num celular baixo (SE) ele ficava
     meio escondido e o erro, que mora logo abaixo dele, ficava fora de vista. */
  useEffect(() => {
    if (origem !== 'Outros') return;
    outrosRef.current?.scrollIntoView({ block: 'nearest' });
    /* Com erro, o aviso mora abaixo do campo: leva a lista até o fim. */
    if (erro && corpoRef.current) corpoRef.current.scrollTop = corpoRef.current.scrollHeight;
  }, [origem, erro]);

  const escolher = (opcao: string) => {
    setOrigem(opcao);
    setErro(null);
    /* Escolher avança sozinho — menos em "Outros", que ainda pede o texto. */
    if (opcao !== 'Outros' && indice < total - 1) irPara(indice + 1);
  };

  /* ── envio ──────────────────────────────────────────────────────────────── */

  const enviar = async () => {
    if (!token || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const segmento = origem === 'Outros' ? origemOutros.trim() : origem;
      const r = await PublicLandingPageService.registerContact(token, {
        nome: nome.trim(),
        telefone: telefone,
        email: email.trim() || undefined,
        segment: segmento || undefined,
        city: cidade.trim() || undefined,
      });

      const guardado: CupomGuardado = {
        codigo: r.promoCode || '',
        texto: r.promoText,
        nome: nome.trim(),
      };
      if (guardado.codigo) guardarCupom(token, guardado);
      setCupom(guardado);
    } catch (e: any) {
      setErro(e?.message || 'Não deu para enviar agora. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  };

  const recomecar = () => {
    if (token) esquecerCupom(token);
    setCupom(null);
    setNome('');
    setTelefone('');
    setOrigem('');
    setOrigemOutros('');
    setEmail('');
    setCidade('');
    setCopiado(false);
    irPara(0);
  };

  const copiar = async () => {
    if (!cupom?.codigo) return;
    try {
      await navigator.clipboard.writeText(cupom.codigo);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* Sem permissão de área de transferência: o código está na tela, em 38px.
         Não vale um aviso de erro para algo que a pessoa consegue ler e digitar. */
    }
  };

  /* ── tema do lojista ────────────────────────────────────────────────────── */

  const primaria = config?.primaryColor || '#0a5c5a';
  const fundo = config?.backgroundColor || '#0d0f12';

  /* A primária é ótima como FUNDO do botão — lá a tinta por cima é derivada e o
     contraste está garantido. Como COR DE TEXTO sobre a página ela não tem
     garantia nenhuma: o rosa de uma empresa real (#eb8fae) sobre fundo branco dá
     2,03:1 no código do cupom, que é a informação mais importante da tela.
     Então a cor da marca só é usada onde ela alcança o contraste do lugar, e o
     resto cai numa tinta derivada do próprio fundo. A marca nunca some da tela:
     ela continua no botão e no quadrado do logo. */
  const contra = razaoDeContraste(primaria, fundo) ?? 0;
  const seguro = tintaSobre(fundo);
  const realce = contra >= 4.5 ? primaria : seguro;      // texto pequeno e médio
  const realceForte = contra >= 3 ? primaria : seguro;   // texto grande e traço

  const tema = {
    '--cap-pri': primaria,
    '--cap-bg': fundo,
    '--cap-tx': config?.textColor || '#ffffff',
    '--cap-ink': tintaSobre(primaria),
    '--cap-realce': realce,
    '--cap-realce-forte': realceForte,
  } as React.CSSProperties;

  const marca = config?.branchName || '';

  const Topo = ({ comPasso }: { comPasso: boolean }) => (
    <>
      <header className="cap-topo">
        {config?.logoUrl ? (
          <img src={getMidiaUrl(config.logoUrl)} alt="" className="cap-logo" />
        ) : (
          <div className="cap-logo cap-logo-vazia" aria-hidden="true">
            {marca ? marca.trim().charAt(0) : <LuBuilding2 size={18} />}
          </div>
        )}
        <div className="cap-marca">{marca}</div>
        {comPasso && (
          <div className="cap-passo">{indice + 1} de {total}</div>
        )}
      </header>
      {comPasso && (
        <div className="cap-prog" aria-hidden="true">
          {passos.map((p, i) => (
            <i key={p} className={i <= indice ? 'on' : undefined} />
          ))}
        </div>
      )}
    </>
  );

  const Erro = () =>
    erro ? (
      <p className="cap-erro" role="alert">
        <LuCircleAlert size={15} />
        <span>{erro}</span>
      </p>
    ) : null;

  /* ── telas ──────────────────────────────────────────────────────────────── */

  if (carregando) {
    return (
      <div className="cap-raiz cap-fixa" style={tema}>
        <div className="cap-aviso">
          <div className="cap-girando" />
          <p>Abrindo a página da loja…</p>
        </div>
      </div>
    );
  }

  if (falhou || !config) {
    return (
      <div className="cap-raiz cap-fixa" style={tema}>
        <div className="cap-aviso">
          <LuBuilding2 size={34} />
          <h1>Este link não está mais ativo</h1>
          <p>Peça o QR Code atualizado no balcão.</p>
        </div>
      </div>
    );
  }

  if (noCupom) {
    const voltando = cupom.codigo && !enviando && indice === 0 && nome === '';
    return (
      <div className="cap-raiz cap-fixa" style={tema}>
        <Topo comPasso={false} />
        <div className="cap-corpo" ref={corpoRef as React.RefObject<HTMLDivElement>}>
          <h1 className="cap-q">
            {voltando ? 'Seu cupom está aqui.' : `Pronto, ${primeiroNome(cupom.nome)}.`}
          </h1>
          <p className="cap-aj">
            {cupom.codigo
              ? 'Mostre este código no caixa.'
              /* Sem cupom, o que fica é a mensagem do lojista — e só. Não vale
                 inventar um botão de WhatsApp: a empresa pode não ter número
                 publicável. */
              : (config?.thanksMessage || 'Seu cadastro foi registrado. Obrigado!')}
          </p>

          {cupom.codigo && (
            <>
              <div className="cap-cod">
                <div className="cap-cod-lb">Seu cupom</div>
                <div className={`cap-cod-v${cupom.codigo.length > 12 ? ' cap-cod-longo' : ''}`}>{cupom.codigo}</div>
                {cupom.texto && <p className="cap-cod-tx">{cupom.texto}</p>}
              </div>

              <button type="button" className="cap-copiar" onClick={copiar}>
                {copiado ? <LuCheck size={16} /> : <LuCopy size={16} />}
                <span>{copiado ? 'Código copiado' : 'Copiar código'}</span>
              </button>

              <p className="cap-rod">
                <LuShieldCheck size={14} />
                <span>Este código fica guardado neste link. Pode fechar e voltar depois.</span>
              </p>
            </>
          )}

          <button type="button" className="cap-outra" onClick={recomecar}>
            Não é você? Cadastrar outra pessoa
          </button>
        </div>
      </div>
    );
  }

  const opcoes =
    config.configuredSegments && config.configuredSegments.length > 0
      ? config.configuredSegments
      : SEGMENTOS_PADRAO;

  const rotuloDaAcao = indice === total - 1
    ? (config.promoActive ? 'Quero meu cupom' : 'Enviar meus dados')
    : 'Continuar';

  return (
    <div className="cap-raiz cap-fixa" style={tema}>
      <Topo comPasso={true} />

      <form
        className={`cap-corpo${passo === 'origem' ? ' cap-rola' : ''}`}
        ref={corpoRef as React.RefObject<HTMLFormElement>}
        onSubmit={(e) => {
          e.preventDefault();
          avancar();
        }}
      >
        {passo === 'nome' && (
          <>
            {/* A promessa vem antes do esforço: é aqui que a pessoa decide se
                vale preencher. Antes ela só aparecia depois do formulário. */}
            {/* O que a pessoa lê antes de decidir se preenche. Com o cupom
                ligado, o próprio cupom é o argumento; sem ele, vale o texto que
                o lojista escrever. A frase fixa de antes prometia desconto
                mesmo com o cupom desligado. */}
            <div className="cap-promessa">
              <p className="cap-pr-t">
                {config.headline
                  ? config.headline
                  : config.promoActive && config.promoText
                    ? <>Ganhe <u>{config.promoText}</u></>
                    : <>Cadastre-se na {marca}</>}
              </p>
              <p className="cap-pr-s">
                {config.subheadline || 'Leva 30 segundos. Só o nome e o WhatsApp são obrigatórios.'}
              </p>
            </div>

            <h1 className="cap-q">Como você se chama?</h1>
            <p className="cap-aj">É o nome que vai no cupom.</p>
            <input
              className={`cap-campo${erro ? ' cap-invalido' : ''}`}
              type="text"
              inputMode="text"
              autoComplete="name"
              autoCapitalize="words"
              placeholder="Seu nome"
              aria-label="Seu nome"
              value={nome}
              onChange={(e) => { setNome(e.target.value); setErro(null); }}
            />
            <Erro />
          </>
        )}

        {passo === 'telefone' && (
          <>
            <h1 className="cap-q">Qual é o seu WhatsApp?</h1>
            <p className="cap-aj">
              É por onde a loja avisa das promoções. Não usamos para mais nada.
            </p>
            <input
              className={`cap-campo${erro ? ' cap-invalido' : ''}`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(00) 00000-0000"
              aria-label="Seu WhatsApp"
              value={telefone}
              onChange={(e) => { setTelefone(formatarTelefone(e.target.value)); setErro(null); }}
            />
            <Erro />
          </>
        )}

        {passo === 'origem' && (
          <>
            <h1 className="cap-q">Como você conheceu a gente?</h1>
            <div className="cap-ops">
              {opcoes.map((op) => (
                <button
                  key={op}
                  type="button"
                  className={`cap-op${origem === op ? ' on' : ''}`}
                  aria-pressed={origem === op}
                  onClick={() => escolher(op)}
                >
                  <span>{op}</span>
                  {origem === op && <LuCheck className="cap-mk" size={17} />}
                </button>
              ))}
            </div>

            {origem === 'Outros' && (
              <input
                ref={outrosRef}
                className={`cap-campo${erro ? ' cap-invalido' : ''}`}
                type="text"
                placeholder="Conte como nos conheceu"
                aria-label="Conte como nos conheceu"
                value={origemOutros}
                onChange={(e) => { setOrigemOutros(e.target.value); setErro(null); }}
              />
            )}
            <Erro />
          </>
        )}

        {passo === 'extras' && (
          <>
            <h1 className="cap-q">
              {pedeEmail && pedeCidade ? 'Quer receber as novidades?'
                : pedeEmail ? 'Quer receber as novidades por e-mail?'
                  : 'De onde você é?'}
            </h1>
            <p className="cap-aj">
              {pedeEmail && pedeCidade ? 'Os dois são opcionais. Dá para pular.' : 'É opcional. Dá para pular.'}
            </p>

            {pedeEmail && (
              <>
                <label className="cap-lb" htmlFor="cap-email">E-mail</label>
                <input
                  id="cap-email"
                  className="cap-campo"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  placeholder="exemplo@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </>
            )}

            {pedeCidade && (
              <>
                <label className="cap-lb" htmlFor="cap-cidade">Cidade onde mora</label>
                <input
                  id="cap-cidade"
                  className="cap-campo"
                  type="text"
                  autoComplete="address-level2"
                  autoCapitalize="words"
                  placeholder="Sua cidade"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                />
              </>
            )}
            <Erro />
          </>
        )}

        {/* O submit fica dentro do form para o "ir" do teclado funcionar, mas a
            barra é quem se vê. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>

      <div className="cap-barra">
        {passo === 'extras' && (
          <button
            type="button"
            className="cap-btn cap-fantasma"
            onClick={enviar}
            disabled={enviando}
          >
            Pular
          </button>
        )}
        <button
          type="button"
          className="cap-btn"
          onClick={avancar}
          disabled={enviando}
        >
          {enviando ? 'Enviando…' : rotuloDaAcao}
          {!enviando && indice < total - 1 && <LuArrowRight size={17} />}
        </button>
      </div>
    </div>
  );
};

export default PublicLandingPage;
